'use strict';

import extend from 'lodash/extend';

/**
 * Low-level communication handler for the Image metadata service.
 * Uses Q-based promises for all operations.
 */

(function($) {
  window.atex = window.atex || {};
  window.atex.aceui = window.atex.aceui || {};
  window.atex.aceui.service = window.atex.aceui.service || {};

  /**
   * Creates a new ImageMetadataService object.
   *
   * Argument 'imageMetadataServiceUrl' - The URL to the Image metadata service.
   * Argument 'serviceSettings' - An object containing settings for this service.
   *
   * The 'serviceSettings' object should look like the following:
   *
   * {
   *   customQ: customQObject
   * }
   *
   * Property 'customQ' is an implementation of a promise based Q library,
   * i.e Angular $q. Defaults to a global Q.
   *
   * Returns a new ImageMetadataService object.
   */
  atex.aceui.service.ImageMetadataService = function(imageMetadataServiceUrl, serviceSettings) {
    var settings = extend({}, serviceSettings);
    var q = settings.customQ || window.Q;

    return {
      /**
       * Extracts and returns EXIF metadata as JSON from the given image file using the
       * Image metadata service.
       *
       * Argument 'transferInfo' - An object containing information on the image to extract
       * the metadata from. The object should look like the following:
       *
       * {
       *   data: BLOB
       * }
       *
       * Property 'data' is the blob object (image file) to upload.
       *
       * NOTE: the Image metadata service relies on the external tool 'exiftool' to be installed.
       *
       * Returns a Q-based promise.
       *
       * If the operation succeeds the returned promise is resolved with an object like:
       *
       * {
       *   data: {
       *     _type: "atex.ImageMetadata",
       *
       *     imageHeight: NUMBER,
       *     imageWidth: NUMBER,
       *
       *     title: 'EXTRACTED TITLE',
       *     byline: 'EXTRACTED BYLINE',
       *     description: 'EXTRACTED DESCRIPTION',
       *
       *     tags: {
       *       Composite: OBJECT,
       *       EXIF: OBJECT,
       *       ExifTool: OBJECT,
       *       File: OBJECT,
       *       IPTC: OBJECT,
       *       JFIF: OBJECT,
       *       Photoshop: OBJECT,
       *       XMP: OBJECT
       *     }
       *   }
       * }
       *
       * Rejects the returned promise with an atex.aceui.error.Error object if the
       * operation fails.
       */
      getImageMetadata: function(transferInfo) {
        var deferred = q.defer();

        var serviceUrl = imageMetadataServiceUrl + '/image';

        var xhr = $.ajaxSettings.xhr();

        xhr.open('POST', serviceUrl, true);

        xhr.setRequestHeader('Accept', 'application/json');
        xhr.setRequestHeader('Content-Type', 'image/jpeg');

        xhr.onload = function(e) {
          if (this.status === 200) {
            deferred.resolve({
              data: JSON.parse(xhr.response)
            });
          } else {
            var error = atex.aceui.HTTPUtils.createError(xhr, 'atex.aceui.service.ImageService', {
              'default': 'There was an error while extracting image metadata!'
            });

            deferred.reject(error);
          }
        };

        xhr.onerror = function(event) {
          var error = atex.aceui.HTTPUtils.createError(xhr, 'atex.aceui.service.ImageMetadataService', {
            'default': 'There was an error while extracting image metadata!'
          });

          deferred.reject(error);
        };

        xhr.ontimeout = function(event) {
          var error = atex.aceui.error.Error({
            origin: 'atex.aceui.service.ImageMetadataService',
            cause: 'Error while extracting image metadata: request timed out!',
            type: atex.aceui.error.types.TIMEOUT
          });

          deferred.reject(error);
        };

        xhr.onabort = function(event) {
          var error = atex.aceui.error.Error({
            origin: 'atex.aceui.service.ImageMetadataService',
            cause: 'Error while extracting image metadata: request aborted!',
            type: atex.aceui.error.types.OPERATION_CANCELLED
          });

          deferred.reject(error);
        };

        xhr.send(transferInfo.data);

        return deferred.promise;
      }
    };
  };
})(jQuery);